import translate from '@translate';
import React, { FC } from 'react';
import Grid, { GridItem } from 'src/components/Grid';
import Select from 'src/components/Select';
import TextField from 'src/components/TextField';
import { OrderType } from 'src/types/order';
import getCountryOptions from 'src/utils/countryOptions';
import { BuildingIcon } from 'src/components/SkladonIcon/SmallIcons';
import { DeliveredIcon } from 'src/components/SkladonIcon/BigIcons';
import Typography from 'src/components/Typography';
import { styled } from '@mui/system';
import { TypographyProps } from '@mui/material/Typography';
import Button from 'src/components/Button';
import { ProviderCompanyType } from 'src/types/providerCompany';
import { sizes } from 'src/themes/default';
import { FormErrorsType, OnChangeType } from '../../types/global';
import { EditKeysType } from './constants';
import { isDhlD2svpServiceCode } from 'skladon-utils';
export interface RecipientPropsType {
    onChange: OnChangeType<EditKeysType>;
    editData: OrderType;
    editErrors: FormErrorsType;
    disableEdit: boolean;
    onSetProviderCompany: () => void;
    providerCompaniesData: null | ProviderCompanyType[];
    summary?: boolean;
}

const isRecipientAddressRequired = (editData: OrderType): boolean => {
    if (editData.SHIPPER === 'DHL' && isDhlD2svpServiceCode(editData.SHIPPER_SERVICE_CODE)) {
        return true;
    }
    return !editData.ADDRESS_ID;
};

export const StyledIcon = styled('div')({
    display: 'inline-block',
    verticalAlign: 'top',

    '& svg': {
        width: '40px',
        height: '40px',
        marginRight: sizes.body_PD,
    },
});

export const IconTextStyle: TypographyProps['sx'] = {
    display: 'inline-block',
    height: '40px',
    lineHeight: '40px',
    verticalAlign: 'middle',
};

const Recipient: FC<RecipientPropsType> = (props) => {
    return (
        <Grid>
            <GridItem cols={12} sx={{ marginBottom: '14px' }}>
                <Typography variant="h2" gutterBottom>
                    {translate('orderDetail.recipientInfo')}
                    {!props.summary && !!props.providerCompaniesData?.length && !props.disableEdit && (
                        <Button
                            onClick={props.onSetProviderCompany}
                            className="order-select-provider-button"
                            mobileFullWidht
                        >
                            {translate('orderDetail.selectProviderRecipient')}
                        </Button>
                    )}
                </Typography>
            </GridItem>
            <GridItem cols={6}>
                <Grid>
                    <GridItem cols={12} sx={{ marginBottom: '14px' }}>
                        <StyledIcon>
                            <BuildingIcon />
                        </StyledIcon>
                        <Typography variant="h3" gutterBottom sx={IconTextStyle}>
                            {translate('orderDetail.contact')}
                        </Typography>
                    </GridItem>
                    <GridItem cols={6}>
                        <TextField
                            value={props.editData.RECIPIENT_NAME}
                            error={props.editErrors.RECIPIENT_NAME}
                            onChange={(value, error) => props.onChange('RECIPIENT_NAME', value, error)}
                            name="RECIPIENT_NAME"
                            label={translate('orderDetail.fullName')}
                            fullWidth
                            required
                            multiline
                            disabled={props.disableEdit || props.summary}
                            maxLength={35}
                        />
                    </GridItem>
                    <GridItem cols={6}>
                        <TextField
                            value={props.editData.RECIPIENT_ATTENTION_NAME}
                            error={props.editErrors.RECIPIENT_ATTENTION_NAME}
                            onChange={(value, error) => props.onChange('RECIPIENT_ATTENTION_NAME', value, error)}
                            name="RECIPIENT_ATTENTION_NAME"
                            label={translate('orderDetail.attentionName')}
                            fullWidth
                            required
                            multiline
                            disabled={props.disableEdit || props.summary}
                            maxLength={35}
                        />
                    </GridItem>
                    <GridItem cols={6}>
                        <TextField
                            value={props.editData.RECIPIENT_COMPANY_NAME}
                            error={props.editErrors.RECIPIENT_COMPANY_NAME}
                            onChange={(value, error) => props.onChange('RECIPIENT_COMPANY_NAME', value, error)}
                            name="RECIPIENT_COMPANY_NAME"
                            label={translate('orderDetail.companyName')}
                            fullWidth
                            multiline
                            disabled={props.disableEdit || props.summary}
                            maxLength={35}
                        />
                    </GridItem>
                    <GridItem cols={6}>
                        <TextField
                            value={props.editData.RECIPIENT_GLN}
                            error={props.editErrors.RECIPIENT_GLN}
                            onChange={(value, error) => props.onChange('RECIPIENT_GLN', value, error)}
                            name="RECIPIENT_GLN"
                            label={translate('orderDetail.GLN')}
                            fullWidth
                            disabled={props.disableEdit || props.summary}
                            maxLength={35}
                        />
                    </GridItem>
                    <GridItem cols={6}>
                        <TextField
                            value={props.editData.RECIPIENT_EMAIL}
                            error={props.editErrors.RECIPIENT_EMAIL}
                            onChange={(value, error) => props.onChange('RECIPIENT_EMAIL', value, error)}
                            name="RECIPIENT_EMAIL"
                            label={translate('orderDetail.email')}
                            fullWidth
                            multiline
                            required
                            disabled={props.disableEdit || props.summary}
                            type="email"
                            maxLength={50}
                        />
                    </GridItem>
                    <GridItem cols={6}>
                        <TextField
                            value={props.editData.RECIPIENT_PHONE}
                            error={props.editErrors.RECIPIENT_PHONE}
                            onChange={(value, error) => props.onChange('RECIPIENT_PHONE', value, error)}
                            name="RECIPIENT_PHONE"
                            label={translate('orderDetail.phone')}
                            fullWidth
                            required
                            multiline
                            disabled={props.disableEdit || props.summary}
                            maxLength={14}
                        />
                    </GridItem>
                    <GridItem cols={6}>
                        <TextField
                            value={props.editData.RECIPIENT_COMPANY_ID}
                            error={props.editErrors.RECIPIENT_COMPANY_ID}
                            onChange={(value, error) => props.onChange('RECIPIENT_COMPANY_ID', value, error)}
                            name="RECIPIENT_COMPANY_ID"
                            label={translate('orderDetail.recipientCompanyId')}
                            fullWidth
                            multiline
                            disabled={props.disableEdit || props.summary}
                            maxLength={14}
                        />
                    </GridItem>
                </Grid>
            </GridItem>
            <GridItem cols={6}>
                <Grid>
                    <GridItem cols={12} sx={{ marginBottom: '14px' }}>
                        <StyledIcon>
                            <DeliveredIcon />
                        </StyledIcon>
                        <Typography variant="h3" gutterBottom sx={IconTextStyle}>
                            {translate('orderDetail.placeOfDelivery')}
                        </Typography>
                    </GridItem>
                    <GridItem cols={6}>
                        <TextField
                            value={props.editData.RECIPIENT_STREET}
                            error={props.editErrors.RECIPIENT_STREET}
                            onChange={(value, error) => props.onChange('RECIPIENT_STREET', value, error)}
                            name="RECIPIENT_STREET"
                            label={translate('orderDetail.recipientStreet')}
                            fullWidth
                            required={isRecipientAddressRequired(props.editData)}
                            multiline
                            disabled={props.disableEdit || props.summary}
                            maxLength={35}
                        />
                    </GridItem>
                    <GridItem cols={6}>
                        <TextField
                            value={props.editData.RECIPIENT_CITY}
                            error={props.editErrors.RECIPIENT_CITY}
                            onChange={(value, error) => props.onChange('RECIPIENT_CITY', value, error)}
                            name="RECIPIENT_CITY"
                            label={translate('orderDetail.city')}
                            fullWidth
                            required={isRecipientAddressRequired(props.editData)}
                            multiline
                            disabled={props.disableEdit || props.summary}
                            maxLength={30}
                        />
                    </GridItem>
                    <GridItem cols={6}>
                        <TextField
                            value={props.editData.RECIPIENT_POST_CODE}
                            error={props.editErrors.RECIPIENT_POST_CODE}
                            onChange={(value, error) => props.onChange('RECIPIENT_POST_CODE', value, error)}
                            name="RECIPIENT_POST_CODE"
                            label={translate('orderDetail.postCode')}
                            fullWidth
                            required={isRecipientAddressRequired(props.editData)}
                            disabled={props.disableEdit || props.summary}
                            maxLength={9}
                        />
                    </GridItem>
                    <GridItem cols={6}>
                        <Select
                            options={getCountryOptions()}
                            value={props.editData.RECIPIENT_STATE}
                            error={props.editErrors.RECIPIENT_STATE}
                            onChange={(value, error) => props.onChange('RECIPIENT_STATE', value, error)}
                            name="RECIPIENT_STATE"
                            label={translate('orderDetail.country')}
                            fullWidth
                            required={isRecipientAddressRequired(props.editData)}
                            disabled={props.disableEdit || props.summary}
                        />
                    </GridItem>
                    <GridItem cols={6}>
                        <TextField
                            value={props.editData.RECIPIENT_ADDRESS_ADDITIONAL_INFO}
                            error={props.editErrors.RECIPIENT_ADDRESS_ADDITIONAL_INFO}
                            onChange={(value, error) =>
                                props.onChange('RECIPIENT_ADDRESS_ADDITIONAL_INFO', value, error)
                            }
                            name="RECIPIENT_ADDRESS_ADDITIONAL_INFO"
                            label={translate('orderDetail.addressAdditionalInfo')}
                            fullWidth
                            multiline
                            disabled={props.disableEdit || props.summary}
                            maxLength={35}
                        />
                    </GridItem>
                    <GridItem cols={6}>
                        <TextField
                            value={props.editData.RECIPIENT_PROVINCE_CODE}
                            error={props.editErrors.RECIPIENT_PROVINCE_CODE}
                            onChange={(value, error) => props.onChange('RECIPIENT_PROVINCE_CODE', value, error)}
                            name="RECIPIENT_PROVINCE_CODE"
                            label={translate('orderDetail.provinceCode')}
                            fullWidth
                            disabled={props.disableEdit || props.summary}
                            maxLength={35}
                        />
                    </GridItem>
                </Grid>
            </GridItem>
        </Grid>
    );
};

export default Recipient;
